import { useAppSelector } from "@/store/hooks"
import { socket } from "@/utils/utils"
import { useNavigate } from "react-router-dom"
import classNames from "classnames"

interface LobbyGameListProps {
  // Lets the empty state offer the same action as the menu's Custom Lobby
  // button, so a player who finds nothing to join can create the missing game
  // without hunting for it.
  onCreateGame?: () => void
  canCreateGame?: boolean
  // The lobby owns where this panel sits in its grid (which column, what
  // order); the panel only owns what it looks like.
  className?: string
}

// Filled/open seats as dots, so how full a game is reads before the numbers do.
const SeatPips: React.FC<{ taken: number; total: number }> = ({
  taken,
  total,
}) => (
  <span className="flex items-center gap-1">
    {Array.from({ length: total }).map((_, i) => (
      <span
        key={i}
        className={classNames(
          "h-1.5 w-1.5 rounded-full",
          i < taken ? "bg-amber-400" : "bg-white/20"
        )}
      />
    ))}
  </span>
)

const LobbyGameList: React.FC<LobbyGameListProps> = ({
  onCreateGame,
  canCreateGame = true,
  className,
}) => {
  const gameList = useAppSelector((state) => state.global.gameList)
  const nav = useNavigate()
  const openGames = gameList.filter((game) => !game.situation)

  const onClickOnJoinGame = (game: IGame) => {
    socket.emit("joinRoom", { roomid: game.id }, (success) => {
      if (success) {
        nav(`/newgame/${game.id}`)
      }
    })
  }

  return (
    <aside
      className={classNames(
        "flex w-[34rem] max-w-[90vw] flex-col overflow-hidden rounded-2xl border border-white/10 bg-black/40 shadow-[0_8px_40px_rgba(0,0,0,0.35)] backdrop-blur-md sm:h-full sm:min-h-0 sm:w-auto sm:min-w-0 sm:max-w-none sm:flex-1 xl:h-full xl:min-h-0 xl:w-auto",
        // Docked beside the menu there is always room for the "nothing here
        // yet" card, and it doubles as an invitation to host. On a phone the
        // row is only ~250px per column, so an empty panel would spend a third
        // of the screen saying nothing — dropping out here is also what turns
        // the mobile lobby from three columns into two.
        openGames.length === 0 && "hidden xl:flex",
        className
      )}
    >
      <div className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-3.5 sm:px-[10px] sm:py-[7px]">
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-white/50 sm:text-[10px]">
          Open Games
        </span>
        <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-2.5 py-1 text-xs font-bold text-white/80 sm:px-[7px] sm:py-[2px] sm:text-[11px]">
          <span
            className={classNames(
              "h-1.5 w-1.5 rounded-full",
              openGames.length > 0 ? "animate-pulse bg-green-400" : "bg-white/30"
            )}
          />
          {openGames.length}
        </span>
      </div>

      <div
        data-testid="newgame.list"
        className="flex max-h-[45vh] flex-col gap-2.5 overflow-y-auto p-3 sm:max-h-none sm:min-h-0 sm:flex-1 sm:gap-[6px] sm:p-[6px] xl:max-h-none xl:min-h-0 xl:flex-1"
      >
        {openGames.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/5 ring-1 ring-white/10">
              <i className="fas fa-users-slash text-lg text-white/30" />
            </span>
            <p className="text-base font-semibold text-white/70">
              No open games right now
            </p>
            <p className="text-sm leading-snug text-white/40">
              Start a Custom Lobby and your game shows up here for everyone else
              to join.
            </p>
            {onCreateGame && (
              <button
                onClick={onCreateGame}
                disabled={!canCreateGame}
                className={classNames(
                  "mt-1 rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-4 py-2 text-sm font-bold text-emerald-200 transition-colors duration-200",
                  canCreateGame
                    ? "hover:border-emerald-300/60 hover:bg-emerald-500/20"
                    : "cursor-not-allowed opacity-50"
                )}
              >
                <i className="fas fa-plus mr-2 text-xs" />
                Create a game
              </button>
            )}
          </div>
        ) : (
          openGames.map((game) => (
            <div
              key={game.id}
              data-testid="newgame.list-item"
              id={`newgame-list-item-${game.id}`}
              className="game-item flex flex-col gap-2.5 rounded-xl border border-white/10 bg-white/5 p-3.5 transition-colors duration-200 hover:border-white/20 hover:bg-white/10 sm:gap-[6px] sm:p-[8px]"
            >
              <div className="flex items-start justify-between gap-2">
                <div
                  data-testid="newgame.title"
                  className="min-w-0 flex-1 truncate text-base font-bold text-white sm:text-[13px]"
                >
                  {game.settings.title}
                </div>
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  {game.settings.isQuickGame && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-200"
                      title="Quick game — less time between actions"
                    >
                      <i className="fas fa-bolt text-[8px]" />
                      Quick
                    </span>
                  )}
                  {game.isPrivate && (
                    <i
                      className="fas fa-lock text-xs text-white/40"
                      title="Private game — needs a password"
                    />
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-1.5 sm:gap-[4px]">
                  <SeatPips
                    taken={game.players.length}
                    total={game.settings.maxPlayers}
                  />
                  <div
                    data-testid="newgame.players"
                    className="text-xs font-semibold text-white/50 sm:text-[10px]"
                  >
                    {game.players.length} / {game.settings.maxPlayers} Players
                  </div>
                </div>
                <button
                  data-testid="newgame.join-btn"
                  onClick={() => onClickOnJoinGame(game)}
                  className="flex-shrink-0 rounded-lg border border-yellow-400/60 bg-gradient-to-r from-yellow-500 via-orange-500 to-yellow-600 px-5 py-2 text-sm font-bold text-white shadow-lg transition-all duration-200 hover:from-yellow-600 hover:via-orange-600 hover:to-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-300 sm:px-[16px] sm:py-[10px] sm:text-[13px]"
                  style={{ boxShadow: "0 4px 18px 0 rgba(255, 193, 7, 0.25)" }}
                >
                  Join Game
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </aside>
  )
}

export default LobbyGameList
